/**
 * src/lib/voice/audioGuards.js — TICKET-1079.
 *
 * CEO 2026-10-02: a deal-card voice note came out 110 bytes (a WebM header, no
 * audio). Nothing stopped it: the recorder only rejected ZERO chunks, the queue
 * sent it to Whisper, Whisper answered 400 "Audio file might be corrupted or
 * unsupported", and the queue retried the same file 10 times over 6 minutes —
 * ten paid calls behind a spinner that looked stuck. One place decides what
 * counts as audio and which failures can never succeed on retry.
 */

// A header-only WebM/MP4 is ~100-500 bytes; one second of 48 kbps Opus is ~6 KB.
export const MIN_AUDIO_BYTES = 1024;

export function isTooSmallAudio(blob) {
  const size = typeof blob?.size === 'number' ? blob.size : 0;
  return size < MIN_AUDIO_BYTES;
}

// Failures that are a property of the FILE, not of the moment: the same bytes
// will fail the same way on every retry. Network blips, timeouts, 429 and 5xx
// are NOT here — those stay retryable.
const PERMANENT_PATTERNS = [
  /corrupted or unsupported/i,
  /invalid file format/i,
  /unsupported (audio )?(format|file)/i,
  /\bHTTP 400\b/i,
  /\bHTTP 413\b/i,
  /empty recording/i,
  /missing audio_blob/i,
];

export function isPermanentVoiceError(err) {
  const msg = err instanceof Error ? (err.message || '') : String(err || '');
  return PERMANENT_PATTERNS.some((rx) => rx.test(msg));
}

// First container the browser can actually record. Hard-coding webm/opus fails
// or degrades on Safari, which records mp4.
export function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  const prefs = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const m of prefs) {
    try { if (MediaRecorder.isTypeSupported?.(m)) return m; } catch { /* noop */ }
  }
  return 'audio/webm';
}

export function extensionForMime(mime) {
  const m = String(mime || '');
  if (m.includes('mp4') || m.includes('aac')) return 'm4a';
  if (m.includes('ogg')) return 'ogg';
  return 'webm';
}

// Device context for voice telemetry — the 2026-10-02 failure could not be
// explained because nothing recorded which browser produced the empty file.
export function deviceInfo() {
  if (typeof navigator === 'undefined') return {};
  return { ua: String(navigator.userAgent || '').slice(0, 200) };
}
